import { Popover } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type {
  SourceTypeOption,
  StudyOption,
} from "@/pages/opsmate/components/CustomerSuggestion/KBScopeSelector";

interface ScopeFilterOption {
  id: string;
  label: string;
}

interface ScopeFilterDropdownProps {
  label: string;
  allLabel: string;
  options: ScopeFilterOption[];
  selectedIds: string[] | null;
  onChange: (ids: string[] | null) => void;
}

function ScopeFilterDropdown({
  label,
  allLabel,
  options,
  selectedIds,
  onChange,
}: ScopeFilterDropdownProps) {
  const { t } = useTranslation();
  const allSelected = selectedIds !== null && selectedIds.length === 0;
  const selectedCount = allSelected ? options.length : (selectedIds?.length ?? 0);
  const selectedLabel = useMemo(() => {
    if (allSelected) return allLabel;
    if (selectedIds?.length === 1) {
      return options.find((option) => option.id === selectedIds[0])?.label ?? selectedIds[0];
    }
    return t("opsmate.docs.filterSelectedCount", { count: selectedCount });
  }, [allLabel, allSelected, options, selectedCount, selectedIds, t]);

  const content = (
    <div style={{ minWidth: 220, maxWidth: 300, maxHeight: 260, overflowY: "auto" }}>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "7px 10px",
          cursor: "pointer",
          fontSize: 12,
          color: "#374151",
        }}
      >
        <input
          type="checkbox"
          checked={allSelected}
          style={{ accentColor: "#830051" }}
          onChange={(event) => onChange(event.target.checked ? [] : null)}
        />
        <span style={{ fontWeight: allSelected ? 600 : 400 }}>{allLabel}</span>
      </label>
      <div style={{ margin: "2px 6px", borderTop: "1px solid #f3f4f6" }} />
      {options.map((option) => {
        const checked = allSelected || (selectedIds?.includes(option.id) ?? false);
        return (
          <label
            key={option.id}
            title={option.label}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "7px 10px",
              cursor: "pointer",
              fontSize: 12,
              color: "#374151",
            }}
          >
            <input
              type="checkbox"
              checked={checked}
              style={{ accentColor: "#830051" }}
              onChange={() => {
                const current = allSelected ? options.map((item) => item.id) : (selectedIds ?? []);
                const next = checked
                  ? current.filter((id) => id !== option.id)
                  : [...current, option.id];
                // 与提问区胶囊一致：[] 只表示主动选择 All；逐项选择保留完整 ID，
                // 一个都不选时使用 null。
                onChange(next.length > 0 ? next : null);
              }}
            />
            <span
              style={{
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontWeight: checked ? 600 : 400,
              }}
            >
              {option.label}
            </span>
          </label>
        );
      })}
    </div>
  );

  return (
    <div style={{ flex: "0 0 130px", width: 130, minWidth: 0 }}>
      <Popover content={content} trigger="click" placement="bottomLeft" arrow={false}>
        <button
          type="button"
          aria-label={`${label}: ${selectedLabel}`}
          style={{
            width: "100%",
            height: 40,
            padding: "0 11px",
            border: "1px solid #d9d9d9",
            borderRadius: 6,
            background: "#fff",
            color: "#111111",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            textAlign: "left",
          }}
        >
          <span
            style={{
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              overflow: "hidden",
            }}
          >
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: 10,
                lineHeight: "12px",
                color: "#8c8c8c",
              }}
            >
              {label}
            </span>
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: 13,
                lineHeight: "16px",
                color: "#111111",
              }}
            >
              {selectedLabel}
            </span>
          </span>
          <i
            className="ri-arrow-down-s-line"
            aria-hidden
            style={{ flexShrink: 0, fontSize: 16, color: "#8c8c8c" }}
          />
        </button>
      </Popover>
    </div>
  );
}

interface AddSourcesScopeFiltersProps {
  sourceTypes: SourceTypeOption[];
  selectedTypes: string[] | null;
  onTypesChange: (ids: string[] | null) => void;
  availableStudies: StudyOption[];
  selectedStudies: string[] | null;
  onStudiesChange: (ids: string[] | null) => void;
  includePersonalDocs: boolean;
  onIncludePersonalDocsChange: (included: boolean) => void;
  showGeneral: boolean;
  showStudies: boolean;
  showPersonal: boolean;
}

export default function AddSourcesScopeFilters({
  sourceTypes,
  selectedTypes,
  onTypesChange,
  availableStudies,
  selectedStudies,
  onStudiesChange,
  includePersonalDocs,
  onIncludePersonalDocsChange,
  showGeneral,
  showStudies,
  showPersonal,
}: AddSourcesScopeFiltersProps) {
  const { t } = useTranslation();
  const studyOptions = useMemo(
    () =>
      availableStudies.map((study) => ({
        id: study.id,
        label: `${study.code} - ${study.name}`,
      })),
    [availableStudies],
  );

  if (!showGeneral && !showStudies && !showPersonal) return null;

  return (
    <div
      style={{
        display: "flex",
        flex: "0 1 auto",
        minWidth: 0,
        gap: 12,
      }}
    >
      {showGeneral ? (
        <ScopeFilterDropdown
          label={t("opsmate.scopeSelector.generalDocs")}
          allLabel={t("opsmate.scopeSelector.allTypes")}
          options={sourceTypes.map((sourceType) => ({
            id: sourceType.id,
            label: sourceType.name,
          }))}
          selectedIds={selectedTypes}
          onChange={onTypesChange}
        />
      ) : null}
      {showStudies ? (
        <ScopeFilterDropdown
          label={t("opsmate.scopeSelector.studySource")}
          allLabel={t("opsmate.scopeSelector.allStudies")}
          options={studyOptions}
          selectedIds={selectedStudies}
          onChange={onStudiesChange}
        />
      ) : null}
      {showPersonal ? (
        <button
          type="button"
          role="switch"
          aria-checked={includePersonalDocs}
          aria-label={t("opsmate.scopeSelector.personalDocs")}
          onClick={() => onIncludePersonalDocsChange(!includePersonalDocs)}
          style={{
            flex: "0 0 170px",
            minWidth: 170,
            height: 40,
            padding: "0 10px",
            border: includePersonalDocs ? "1px solid rgba(131, 0, 80, 0.32)" : "1px solid #d9d9d9",
            borderRadius: 6,
            background: includePersonalDocs ? "rgba(131, 0, 80, 0.04)" : "#fff",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            transition: "border-color 0.2s ease, background-color 0.2s ease",
            textAlign: "left",
          }}
        >
          <span
            style={{
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              gap: 7,
            }}
          >
            <span
              style={{
                width: 24,
                height: 24,
                borderRadius: 6,
                background: includePersonalDocs ? "rgba(131, 0, 80, 0.10)" : "#f3f4f6",
                color: includePersonalDocs ? "#830051" : "#6b7280",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <i className="ri-folder-user-line" aria-hidden style={{ fontSize: 13 }} />
            </span>
            <span style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: 10,
                  lineHeight: "12px",
                  color: "#8c8c8c",
                }}
              >
                {t("opsmate.scopeSelector.personalDocs")}
              </span>
              <span
                style={{
                  fontSize: 9,
                  lineHeight: "11px",
                  color: includePersonalDocs ? "#830051" : "#9ca3af",
                }}
              >
                {t(
                  includePersonalDocs
                    ? "opsmate.docs.personalIncluded"
                    : "opsmate.docs.personalExcluded",
                )}
              </span>
            </span>
          </span>
          <span
            aria-hidden
            style={{
              flexShrink: 0,
              position: "relative",
              width: 28,
              height: 16,
              borderRadius: 999,
              background: includePersonalDocs ? "#830051" : "#d1d5db",
              transition: "background-color 0.2s ease",
            }}
          >
            <span
              style={{
                position: "absolute",
                top: 2,
                left: 2,
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: "#fff",
                boxShadow: "0 1px 2px rgba(0,0,0,0.18)",
                transform: includePersonalDocs ? "translateX(12px)" : "translateX(0)",
                transition: "transform 0.2s ease",
              }}
            />
          </span>
        </button>
      ) : null}
    </div>
  );
}
